import { useEffect } from 'react';
import Box from '@mui/material/Box';
import { useMediaQuery } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector, useDispatch } from 'react-redux';
import './AdminUserList.scss';
import { setSelectedUser, fetchAllUsers } from '../../../../slices/userSlice';
import IconButton from '@mui/material/IconButton';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useState } from 'react';
import { toast } from 'react-toastify';
import AdminUserChickenTest from './AdminUserChickenTest/AdminUserChickenTest';

/**
 * SimpleDataGrid Component
 * 
 * @component
 * @description
 * Displays a read-only table of all users using MUI's 'DataGrid'.
 * Columns include User, Email, Role, Verification Status, Account Lock Status, and User ID.
 * The grid is paginated and styled via 'AdminUserList.scss'.
 * 
 * @returns {JSX.Element} A Material UI 'Box' containing a header and a 'DataGrid' of users.
 * 
 * @example
 * // Usage:
 * import SimpleDataGrid from './AdminGrid';
 * 
 * function AdminUserList() {
 *     return <SimpleDataGrid />
 * }
 */

function AdminUserList() { 

    const dispatch = useDispatch();
    const isDesktop = useMediaQuery('(min-width:1200px) and (min-height: 800px)');
    const rows = useSelector((state) => state.user.usersList);
    const currentUser = useSelector((state) => state.user.user);
    const [chickenTestOpen, setChickenTestOpen] = useState(false);
    const [selectedLockUser, setSelectedLockUser] = useState(null);

    const baseColumns = [
        { field: 'user', headerName: 'User', flex: 1, minWidth: 150, valueGetter: (value, row) => { return `${row.firstName || ""} ${row.lastName || ""}`.trim() || "N/A"}},
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 150 },
        { field: 'roles', headerName: 'Role', flex: 1, minWidth: 150, valueGetter: (value, row) => { const rawRole = row.roles?.[0]?.authority || "ROLE_USER"; const role = rawRole.replace("ROLE_", "").replace("_", "").trim(); return role;} },
        { field: 'emailVerified', headerName: 'Verified', flex: 1, minWidth: 150, valueGetter: (value, row) => row.emailVerified ? "Yes" : "No"},
        { field: 'accountLocked', headerName: 'Account Status', flex: 1, minWidth: 150, renderCell: (params) => {
          const isLocked = params.value; 

          return (
              <IconButton 
                  onClick={(e) => {
                e.stopPropagation();

                if (params.row.id === currentUser?.id) {
                toast.error("You cannot lock your own account.");
                return;
            }

            setSelectedLockUser({
                userId: params.row.id,
                accountLocked: !isLocked,
            });

            setChickenTestOpen(true);
        
                  }}
              >
                  {isLocked ? (
                      <LockIcon sx={{ color: '#d32f2f' }} /> // Red Locked Icon
                  ) : (
                      <LockOpenIcon sx={{ color: '#2e7d32' }} /> // Green Unlocked Icon
                  )}
              </IconButton>
          );
        }},
        { field: 'id', headerName: 'ID', flex: 1, minWidth: 150 }
    ];


    useEffect(() => {
        dispatch(fetchAllUsers());
    }, [dispatch]);

    return (
        <Box className="AdminUserList" data-testid="admin-user-list-container">
            <div className="AdminUserList-header" data-testid="admin-user-list-header">
            </div>
            <Box
                sx={{
                    height: 'calc(100vh - 207px)',
                    width: '100%',
                    overflow: 'hidden',
                }}>
                <DataGrid
                    rows={rows ?? []}
                    columns={baseColumns}
                    /* This uses Redux to tell the whole app which user was clicked */
                    onRowClick={(params) => dispatch(setSelectedUser(params.row))}                    
                    autoPageSize
                    pageSizeOptions={isDesktop ? [10] : []}
                    initialState={isDesktop ? {pagination: { paginationModel: {pageSize: 10} } } : undefined }
                    rowHeight={60}
                    columnHeaderHeight={48}
                    data-testid="admin-user-data-grid"
                    sx={{
                        border: 'none',
                        fontFamily: "'Poppins', sans-serif",
                        '& .MuiDataGrid-virtualScroller': { overflowY: 'hidden' },
                        '& .MuiDataGrid-footerContainer': { padding: '0px !important' },
                        '& .MuiTablePagination-displayedRows': {
                            fontFamily: "'Poppins', sans-serif",
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select': {
                            fontFamily: "'Poppins', sans-serif",
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 600,
                        },
                        '& .MuiDataGrid-cell': {
                            fontFamily: "'Poppins', sans-serif",
                         },
                    }}
                    
                  />
                {chickenTestOpen && selectedLockUser && (
                 <AdminUserChickenTest
                onClose={() => {
                    setChickenTestOpen(false);
                    setSelectedLockUser(null);
                }}
                userId={selectedLockUser.userId}
                accountLocked={selectedLockUser.accountLocked}
            />
                )}
            </Box>
        </Box>
    )
}

export default AdminUserList;